import {
  Component,
  HostBinding,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FmntsFormFieldModule } from '@fmnts/components/form-field';
import {
  FmntsRadioGroup,
  RadioGridItemComponent,
} from '@fmnts/components/radio';
import { getUnique } from '@fmnts/core/collection';
import {
  distinctUntilArrayChanged,
  distinctUntilFormKeysChanged,
} from '@fmnts/core/rxjs';
import {
  AbstractDeclarativeInputFieldComponent,
  ConditionDefinition,
  ConditionalService,
  ErrorMessagePipe,
  FormSelectField,
  SelectInputItem,
} from '@fmnts/declarative-forms';
import { toItemsWithCondition } from '@fmnts/declarative-forms/common';
import { I18nModule } from '@fmnts/i18n';
import { LetDirective } from '@ngrx/component';
import {
  Observable,
  OperatorFunction,
  debounceTime,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { RadioItem } from '../declarative-radio-item.model';

type FieldValue = unknown;

@Component({
  selector: 'declarative-radio-content-field',
  templateUrl: './radio-content-field.component.html',
  styleUrls: ['./radio-content-field.component.scss'],
  standalone: true,
  imports: [
    I18nModule,
    FormsModule,
    ErrorMessagePipe,
    LetDirective,
    FmntsFormFieldModule,
    FmntsRadioGroup,
    RadioGridItemComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class DeclarativeRadioContentFieldComponent
  extends AbstractDeclarativeInputFieldComponent<FormSelectField, FieldValue>
  implements OnInit
{
  private readonly conditionalService = inject(ConditionalService);

  @HostBinding('class') componentClass = 'form-field form-field__radio-content';

  protected get value(): FieldValue {
    return this._value;
  }
  protected set value(value: FieldValue) {
    this.changeValue(value);
    this._onTouched();
  }
  private _value: FieldValue;

  /** Items that are visible. */
  protected items$!: Observable<SelectInputItem[]>;

  ngOnInit(): void {
    this.items$ = getItems$(this.field).pipe(
      switchMap((allItems) => this._getVisibleItems$(allItems)),
    );
  }

  writeValue(value: unknown): void {
    this._value = value;
  }

  protected get showError(): boolean {
    return this.control.touched && this.control.invalid;
  }

  private changeValue(value: FieldValue) {
    this._value = value;
    this._onChange(value);
  }

  private _getVisibleItems$(allItems: RadioItem[]): Observable<RadioItem[]> {
    // First extract all relevant field keys
    const observableKeys = getUnique(
      allItems.flatMap(({ condition }) =>
        condition
          ? this.conditionalService.extractReferencedKeys(condition)
          : [],
      ),
    );

    // Check if the field or any referenced field is dirty
    const hasDirtyField = [this.field.key, ...observableKeys].some((k) =>
      this.form.getControlsByKey(k).some((c) => c.dirty),
    );

    const formValueChanges$ = this.form.flatValueChanges.pipe(
      debounceTime(100),
    );

    const source$ = hasDirtyField
      ? formValueChanges$.pipe(startWith(this.form.flatValue))
      : formValueChanges$;

    const items$ = source$.pipe(
      distinctUntilFormKeysChanged(observableKeys),
      this.filterUnfulfilledConditions(allItems),
      tap((filteredItems) => this.selectNextItem(filteredItems)),
    );

    return hasDirtyField ? items$ : items$.pipe(startWith(allItems));
  }

  private filterUnfulfilledConditions(
    items: RadioItem[],
  ): OperatorFunction<Record<string, unknown>, RadioItem[]> {
    return (obs$) =>
      obs$.pipe(
        map((changes) =>
          items.filter((i) => this.checkConditions(changes, i.condition)),
        ),
        distinctUntilArrayChanged((prev, current) =>
          current.every((i) => prev.some((pi) => pi.value === i.value)),
        ),
      );
  }

  /**
   * @param changes Changes to the form
   * @param condition Condition to be fulfilled
   *
   * @returns
   * `true`, if `condition` is fulfilled or no conditions are passed.
   * Otherwise `false`.
   */
  private checkConditions(
    changes: Record<string, unknown>,
    condition: ConditionDefinition | undefined,
  ) {
    return !condition
      ? true
      : this.conditionalService.checkObj(changes, condition);
  }

  /**
   * Selects the item that is next in the order of the original items
   * and that is included in the passed `items`. It will wrap around
   * at the end of the original items.
   *
   * If the current value is null or included in the passed `items`,
   * nothing will happen.
   *
   * If the passed `items` don't contain any element, the value
   * will be set to `null`.
   *
   * @param items The available items
   */
  private selectNextItem(items: SelectInputItem[]) {
    // Do nothing, if value is still null or included in the items
    if (this.value === null || items.some((i) => i.value === this.value)) {
      return;
    }

    // When no items are left, we need to unset the value
    if (items.length === 0) {
      this.changeValue(null);
      return;
    }

    // Select next item
    const { items: allItems } = this.field.widgetOptions;
    const startIndex = allItems.findIndex((i) => i.value === this.value);

    for (let i = 0; i < allItems.length; ++i) {
      // Wrap around at the and of the items array
      const nextIndex = (startIndex + i) % allItems.length;
      const nextItem = allItems[nextIndex];

      if (items.some((item) => item.value === nextItem.value)) {
        this.changeValue(nextItem.value);
        return;
      }
    }
  }
}

/**
 * @returns
 * Observable for items
 */
function getItems$(field: FormSelectField): Observable<RadioItem[]> {
  if (!field) {
    // No items without a field
    return of([]);
  }

  const { items, itemConditions } = field.widgetOptions;
  const allRadioItems = toItemsWithCondition(items, itemConditions ?? {}).map(
    ({ item, condition }) => ({ ...item, condition }) as RadioItem,
  );

  return of(allRadioItems);
}
